import { useTranslation } from "react-i18next";
import { Alert, Badge, Mono } from "@h2j-data-team/ui";
import type { QvdAnalysisCoverage } from "@repo/shared";

import { useFormatDate } from "@/hooks/use-format-date";
import { cn } from "@/lib/utils";

// Discloses the apps the run learned NOTHING about: their reload log was
// inaccessible AND their lineage could not be read either. The results are shown
// in full regardless (an unreadable app no longer withholds the orphan list), so
// this notice is the honest caveat next to them — one of these apps could be the
// sole consumer of a file listed as an orphan. It informs; it blocks nothing.
//
// The total comes from the per-cause counts, which are exact, not from the list,
// which is capped — a big tenant shows the first entries plus how many were left
// out. Older runs carry no counts at all, so they render nothing.
export function UnverifiedAppsNotice({ coverage }: { coverage: QvdAnalysisCoverage | null }) {
  const { t } = useTranslation("qvd-analysis");
  const { formatDate } = useFormatDate();
  if (!coverage) return null;

  const byCause = coverage.appsUnverifiedByCause ?? {};
  const total = Object.values(byCause).reduce((sum, n) => sum + n, 0);
  if (total <= 0) return null;

  const forbidden = byCause.forbidden ?? 0;
  const apps = coverage.appsUnverified ?? [];
  const omitted = coverage.appsUnverifiedOmitted ?? 0;

  const title = t("unverifiedApps.title", { count: total });

  return (
    <Alert tone="warning" title={title}>
      <div className="flex flex-col gap-1">
        <p>{t("unverifiedApps.explain")}</p>
        {forbidden > 0 && <p>{t("unverifiedApps.forbiddenHint", { count: forbidden })}</p>}
      </div>
      <div
        role="region"
        aria-label={title}
        tabIndex={0}
        className="mt-3 max-h-60 overflow-y-auto rounded-md border border-hairline bg-surface px-3 py-1 text-ink -outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
      >
        <ul>
          {apps.map((app) => (
            <li
              key={app.appId}
              className="flex items-center justify-between gap-3 border-b border-hairline-soft py-1.5 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="truncate">{app.appName || app.appId}</div>
                {/* The space NAME is resolved through the space listing, which only
                    shows what the connection identity can see — so the apps blocked
                    by a missing permission are precisely the ones with no name. The
                    raw ID is still searchable in Qlik, so it beats "not identified";
                    that wording is left for an app with neither (personal space). */}
                <div
                  className={cn(
                    "truncate text-xs text-dim",
                    !app.spaceName && app.spaceId && "font-mono",
                  )}
                >
                  {app.spaceName || app.spaceId || t("unverifiedApps.unknownSpace")}
                </div>
                {/* App names repeat across spaces; the ID is what identifies the app
                    and opens it directly in Qlik. */}
                <Mono className="block truncate text-xs text-faint" title={app.appId}>
                  {app.appId}
                </Mono>
                {/* "Reload log unavailable" alone reads as "this app is dead", and
                    it is not: the scan only ever looks at apps that HAVE reloaded,
                    so the log is gone (Qlik expires them), not absent for lack of a
                    run. The date says which. Runs persisted before the field
                    existed carry none, and then the cause badge stands alone. */}
                {app.cause === "no_successful_reload" && app.lastReloadTime && (
                  <div className="text-xs text-dim">
                    {t("unverifiedApps.lastReload", { date: formatDate(app.lastReloadTime) })}
                  </div>
                )}
              </div>
              <Badge variant="secondary" className="shrink-0">
                {t(`unverifiedApps.causes.${app.cause}`, app.cause)}
              </Badge>
            </li>
          ))}
        </ul>
        {omitted > 0 && (
          <p className="py-1.5 text-xs text-dim">
            {t("unverifiedApps.omitted", { count: omitted })}
          </p>
        )}
      </div>
    </Alert>
  );
}
